import React from "react";
import { Testimonial, TestimonialsComponent } from "../components/common/testimonial/TestimonialComponent";
import { JsonLd } from "../components/common/JsonLd";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { PATH_NEUROLOGIE_GERIATRIE } from "../utils/constants";
import pageData from "../data/pages/neurologie-geriatrie.json";
import { mapPageSections } from "../utils/pageContent";
import { buildSeoPageGraph } from "../utils/structured-data";

type Section = {
  title: string;
  content: string[];
  texts?: string[];
  img?: string;
  ratio?: string;
};

const data = {
  sections: mapPageSections(pageData.sections as Section[]),
  testimonials: pageData.testimonials as Testimonial[],
};

export default function NeurologyGeriatricsPage() {
  return (
    <>
      <SEOHelmet page="neurologie" pathname={PATH_NEUROLOGIE_GERIATRIE} />
      <JsonLd
        data={buildSeoPageGraph(PATH_NEUROLOGIE_GERIATRIE, "neurologie", {
          medicalSpecialty: ["Neurologic", "Geriatric"],
          includeFounder: true,
        })}
      />

      <main id="main-content" tabIndex={-1} className="space-y-14">
        {data.sections.map((section, index) => (
          <article key={index}>
            {index === 0 ? (
              <section className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
                    Neurologische und Geriatrische Ergotherapie
                  </h1>
                  <h2 className="sr-only">Behandlungsschwerpunkte</h2>
                  <ul
                    className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-3 mb-8 text-base text-gray-500 dark:text-gray-400"
                    aria-label="Neurologische Erkrankungen"
                  >
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-gray-500 dark:text-gray-400 leading-relaxed">
                        {item}
                      </li>
                    ))}
                  </ul>
                  {section.texts?.map((text, textIndex) => (
                    <p key={textIndex} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                      {text}
                    </p>
                  ))}
                </div>
                {section.img && (
                  <aside>
                    <img
                      src={section.img}
                      alt="Neurologische Ergotherapie Rottweil - Behandlung von Schlaganfall, Parkinson, Multiple Sklerose und altersbedingten Einschränkungen"
                      className={`w-full max-w-sm mx-auto lg:max-w-md rounded-2xl object-cover shadow-sm dark:shadow-xl dark:shadow-black/20 ${section.ratio || ""}`}
                    />
                  </aside>
                )}
              </section>
            ) : (
              <section aria-labelledby={`section-${index}`}>
                <h2
                  id={`section-${index}`}
                  className="text-xl sm:text-2xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-4"
                >
                  {section.title}
                </h2>
                <ul className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-3 mb-8 text-base text-gray-500 dark:text-gray-400">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-gray-500 dark:text-gray-400 leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
                {section.texts?.map((text, textIndex) => (
                  <p key={textIndex} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                    {text}
                  </p>
                ))}
              </section>
            )}
          </article>
        ))}

        <section aria-label="Erfahrungsberichte">
          <h2 className="sr-only">Erfahrungsberichte unserer Klienten</h2>
          <TestimonialsComponent testimonials={data.testimonials} />
        </section>
      </main>
    </>
  );
}
